import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { supabase, Task, Communication, Tables } from "../utils/supabase";
import connectionDebugger from "../utils/debugConnection";
import CalibrationStatus from "../components/CalibrationStatus";
import ExpiryStatus from "../components/ExpiryStatus";
import KpiCharts from "../components/KpiCharts";
import {
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ChatBubbleLeftRightIcon,
  ScaleIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentListIcon,
  PlusIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import { format, isToday, isTomorrow, isPast } from "date-fns";

interface DashboardStats {
  totalTasks: number;
  pendingTasks: number;
  overdueTasks: number;
  completedToday: number;
  urgentTasks: number;
  unresolvedComplaints: number;
  totalComplaints: number;
}

interface DashboardProps {
  onPageChange?: (page: string) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onPageChange }) => {
  console.log("Dashboard component rendering...");
  const { user, selectedPharmacy } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalTasks: 0,
    pendingTasks: 0,
    overdueTasks: 0,
    completedToday: 0,
    urgentTasks: 0,
    unresolvedComplaints: 0,
    totalComplaints: 0,
  });
  const [recentTasks, setRecentTasks] = useState<Task[]>([]);
  const [recentCommunications, setRecentCommunications] = useState<
    Communication[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [fallbackMode, setFallbackMode] = useState(false);
  const [pendingPreparations, setPendingPreparations] = useState(0);
  // I: Bypass mode is only available in development builds
  const [bypassMode] = useState(() => {
    if (!import.meta.env.DEV) return false;
    const urlParams = new URLSearchParams(window.location.search);
    const bypassParam = urlParams.get("bypass") === "true";
    const bypassStorage = localStorage.getItem("dashboardBypass") === "true";
    return bypassParam || bypassStorage;
  });
  // H: Tab state for the recent tasks widget
  const [tasksTab, setTasksTab] = useState<"mine" | "all">("mine");

  useEffect(() => {
    console.log(
      "Dashboard useEffect triggered, selectedPharmacy:",
      selectedPharmacy,
    );

    // If bypass mode is enabled, skip data fetching
    if (bypassMode) {
      console.log("Bypass mode enabled - skipping database queries");
      setStats({
        totalTasks: 12,
        pendingTasks: 5,
        overdueTasks: 2,
        completedToday: 3,
        urgentTasks: 1,
        unresolvedComplaints: 2,
        totalComplaints: 5,
      });
      setRecentTasks([]);
      setRecentCommunications([]);
      setLoading(false);
      return;
    }

    if (selectedPharmacy) {
      // Set fallback mode after 5 seconds if still loading
      const fallbackTimer = setTimeout(() => {
        console.log("Enabling fallback mode due to slow data fetch");
        setFallbackMode(true);
        setLoading(false);
      }, 5000);

      fetchDashboardData().finally(() => {
        clearTimeout(fallbackTimer);
      });
    }
  }, [selectedPharmacy, bypassMode]);

  const fetchDashboardData = async () => {
    console.log(
      "fetchDashboardData called for pharmacy:",
      selectedPharmacy?.name,
    );
    if (!selectedPharmacy) {
      console.log("No selected pharmacy, skipping fetch");
      return;
    }

    // Capture connection state before fetch
    await connectionDebugger.captureConnectionState("Dashboard - Start Fetch");

    setLoading(true);

    // Add timeout protection
    const fetchTimeout = setTimeout(() => {
      console.warn("Dashboard data fetch timeout, forcing completion");
      setLoading(false);
    }, 10000); // 10 second timeout

    try {
      // Fetch tasks with error handling
      console.log("Fetching tasks for pharmacy:", selectedPharmacy.id);
      const { data: tasks, error: tasksError } = await supabase
        .from(Tables.TASKS)
        .select("*")
        .eq("pharmacy_id", selectedPharmacy.id)
        .order("created_at", { ascending: false });

      console.log("Tasks fetch result:", {
        tasks: tasks?.length || 0,
        error: tasksError,
      });

      if (tasksError) {
        console.error("Error fetching tasks:", tasksError);
        clearTimeout(fetchTimeout);
        setLoading(false);
        return;
      }

      // Fetch recent communications with error handling
      const { data: communications, error: communicationsError } =
        await supabase
          .from(Tables.COMMUNICATIONS)
          .select("*")
          .eq("pharmacy_id", selectedPharmacy.id)
          .order("created_at", { ascending: false })
          .limit(5);

      const { data: complaints } = await supabase
        .from(Tables.COMPLAINTS)
        .select("*")
        .eq("pharmacy_id", selectedPharmacy.id)
        .order("created_at", { ascending: false });

      // J: Fetch pending preparations count
      const { data: preparations } = await supabase
        .from(Tables.PREPARATIONS)
        .select("id")
        .eq("pharmacy_id", selectedPharmacy.id)
        .eq("status", "draft");
      setPendingPreparations(preparations?.length ?? 0);

      if (communicationsError) {
        console.error("Error fetching communications:", communicationsError);
      }

      clearTimeout(fetchTimeout);

      if (tasks && Array.isArray(tasks)) {
        try {
          // Calculate stats with safer date handling
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          const pendingTasks = tasks.filter(
            (task) =>
              task.status === "pending" || task.status === "in_progress",
          );

          const overdueTasks = tasks.filter((task) => {
            try {
              return (
                (task.status === "pending" || task.status === "in_progress") &&
                task.due_date &&
                isPast(new Date(task.due_date))
              );
            } catch (e) {
              console.warn("Invalid due_date for task:", task.id);
              return false;
            }
          });

          const completedToday = tasks.filter((task) => {
            try {
              return (
                task.status === "completed" &&
                task.completed_at &&
                isToday(new Date(task.completed_at))
              );
            } catch (e) {
              console.warn("Invalid completed_at for task:", task.id);
              return false;
            }
          });

          const urgentTasks = tasks.filter(
            (task) =>
              task.priority === "urgent" &&
              (task.status === "pending" || task.status === "in_progress"),
          );

          const unresolvedComplaints =
            complaints?.filter(
              (complaint) =>
                complaint.status === "open" ||
                complaint.status === "investigating",
            ) || [];

          setStats({
            totalTasks: tasks.length,
            pendingTasks: pendingTasks.length,
            overdueTasks: overdueTasks.length,
            completedToday: completedToday.length,
            urgentTasks: urgentTasks.length,
            unresolvedComplaints: unresolvedComplaints.length,
            totalComplaints: complaints?.length || 0,
          });

          // Set recent tasks (pending/in progress, sorted by due date)
          const sortedTasks = pendingTasks
            .filter((task) => task.due_date) // Only tasks with valid due dates
            .sort((a, b) => {
              try {
                return (
                  new Date(a.due_date).getTime() -
                  new Date(b.due_date).getTime()
                );
              } catch (e) {
                return 0; // Keep original order if dates are invalid
              }
            })
            .slice(0, 5);

          setRecentTasks(sortedTasks);
        } catch (statsError) {
          console.error("Error processing task statistics:", statsError);
          // Set default stats on error
          setStats({
            totalTasks: 0,
            pendingTasks: 0,
            overdueTasks: 0,
            completedToday: 0,
            urgentTasks: 0,
            unresolvedComplaints: 0,
            totalComplaints: 0,
          });
          setRecentTasks([]);
        }
      } else {
        // Set default stats if no tasks
        setStats({
          totalTasks: 0,
          pendingTasks: 0,
          overdueTasks: 0,
          completedToday: 0,
          urgentTasks: 0,
          unresolvedComplaints: 0,
          totalComplaints: 0,
        });
        setRecentTasks([]);
      }

      if (communications && Array.isArray(communications)) {
        setRecentCommunications(communications);
      } else {
        setRecentCommunications([]);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      clearTimeout(fetchTimeout);

      // Set default values on error
      setStats({
        totalTasks: 0,
        pendingTasks: 0,
        overdueTasks: 0,
        completedToday: 0,
        urgentTasks: 0,
        unresolvedComplaints: 0,
        totalComplaints: 0,
      });
      setRecentTasks([]);
      setRecentCommunications([]);
    } finally {
      setLoading(false);
    }
  };

  const getTaskTypeIcon = (type: string) => {
    switch (type) {
      case "communication":
        return ChatBubbleLeftRightIcon;
      case "calibration":
        return ScaleIcon;
      case "expiry_check":
        return CalendarDaysIcon;
      case "complaint":
        return ExclamationTriangleIcon;
      default:
        return ClockIcon;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "text-red-600 bg-red-50";
      case "high":
        return "text-orange-600 bg-orange-50";
      case "medium":
        return "text-yellow-600 bg-yellow-50";
      case "low":
        return "text-green-600 bg-green-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "text-green-600 bg-green-50";
      case "in_progress":
        return "text-primary-600 bg-primary-50";
      case "pending":
        return "text-yellow-600 bg-yellow-50";
      case "cancelled":
        return "text-gray-600 bg-gray-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const formatDueDate = (dateString: string) => {
    const date = new Date(dateString);
    if (isToday(date)) {
      return "vandaag";
    }
    if (isTomorrow(date)) {
      return "morgen";
    }
    if (isPast(date)) {
      return "verlopen";
    }
    return format(date, "d MMM");
  };

  // B+D+J: Stat cards without fake percentages, each navigates to relevant page
  const statCards = [
    {
      title: "Openstaande Taken",
      value: stats.pendingTasks,
      icon: ClockIcon,
      color: "text-primary-600 bg-primary-50",
      onClick: () => onPageChange?.("tasks"),
    },
    {
      title: "Verlopen Taken",
      value: stats.overdueTasks,
      icon: ExclamationCircleIcon,
      color: "text-red-600 bg-red-50",
      onClick: () => {
        sessionStorage.setItem(
          "task_filter_hint",
          JSON.stringify({ status: "overdue" }),
        );
        onPageChange?.("tasks");
      },
    },
    {
      title: "Vandaag Voltooid",
      value: stats.completedToday,
      icon: CheckCircleIcon,
      color: "text-green-600 bg-green-50",
      onClick: () => {
        sessionStorage.setItem(
          "task_filter_hint",
          JSON.stringify({ status: "completed" }),
        );
        onPageChange?.("tasks");
      },
    },
    {
      title: "Urgente Taken",
      value: stats.urgentTasks,
      icon: ExclamationTriangleIcon,
      color: "text-orange-600 bg-orange-50",
      onClick: () => {
        sessionStorage.setItem(
          "task_filter_hint",
          JSON.stringify({ priority: "urgent" }),
        );
        onPageChange?.("tasks");
      },
    },
    {
      title: "Openstaande Klachten",
      value: stats.unresolvedComplaints,
      icon: ExclamationTriangleIcon,
      color: "text-red-600 bg-red-50",
      onClick: () => onPageChange?.("complaints"),
    },
    {
      title: "Bereidingen in concept",
      value: pendingPreparations,
      icon: ClipboardDocumentListIcon,
      color: "text-purple-600 bg-purple-50",
      onClick: () => onPageChange?.("compounding-preparations"),
    },
  ];

  if (loading) {
    console.log("Dashboard showing loading state");
    return (
      <div className="p-6 sm:p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
              >
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                <div className="h-8 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  console.log("Dashboard rendering main content", {
    user: user?.full_name,
    pharmacy: selectedPharmacy?.name,
    fallbackMode,
    bypassMode,
  });

  if (!user || !selectedPharmacy) {
    console.log("Missing user or pharmacy, showing fallback");
    return (
      <div className="p-6 sm:p-8">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-gray-600 mb-2">
            {!user ? "No user found" : "No pharmacy selected"}
          </h2>
          <p className="text-gray-500">
            {!user
              ? "Please log in to view the dashboard"
              : "Please select a pharmacy to view dashboard data"}
          </p>
        </div>
      </div>
    );
  }

  if (fallbackMode) {
    console.log("Rendering fallback dashboard");
    return (
      <div className="p-6 sm:p-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">
            Welkom terug, {user?.full_name}
          </h1>
          <p className="text-gray-600 mt-1">
            Dashboard wordt geladen... ({selectedPharmacy?.name})
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="text-center">
              <ClockIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Data wordt geladen...</p>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="text-center">
              <ExclamationCircleIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Controleren van taken...</p>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="text-center">
              <CheckCircleIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Statistieken laden...</p>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="text-center">
              <ExclamationTriangleIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Even geduld...</p>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Langzame verbinding
              </h3>
              <p className="text-sm text-yellow-700 mt-1">
                De dashboard data wordt nog geladen. Dit kan even duren bij een
                langzame internetverbinding.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          Welkom terug, {user?.full_name}
        </h1>
        <p className="text-gray-600 mt-1">
          Dit is wat er vandaag gebeurt bij {selectedPharmacy?.name}
        </p>
        {bypassMode && (
          <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <svg
              className="w-3 h-3 mr-1"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            Bypass Mode - Using Mock Data
          </div>
        )}
      </div>

      {/* Stats Cards — C: 6-column grid, D: clickable, B: no fake percentages */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <button
              key={index}
              onClick={stat.onClick}
              className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 hover:shadow-md hover:border-gray-300 transition-all text-left focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              <div className="flex flex-col items-start">
                <div className={`p-2 rounded-lg ${stat.color} mb-3`}>
                  <Icon className="h-5 w-5" />
                </div>
                <p className="text-xs font-medium text-gray-500 leading-tight mb-1">
                  {stat.title}
                </p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Status Monitoring Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <CalibrationStatus />
        <ExpiryStatus />
      </div>

      {/* KPI Trend Charts */}
      <KpiCharts />

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Tasks — H: Mijn taken / Alle taken tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 pt-6 pb-0 border-b border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">
                Aankomende Taken
              </h2>
              <button
                onClick={() => onPageChange?.("tasks")}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center"
              >
                Alles bekijken
                <ArrowRightIcon className="ml-1 h-4 w-4" />
              </button>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => setTasksTab("mine")}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  tasksTab === "mine"
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                Mijn taken
              </button>
              <button
                onClick={() => setTasksTab("all")}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  tasksTab === "all"
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                Alle taken
              </button>
            </div>
          </div>
          <div className="p-6">
            {(() => {
              const displayedTasks =
                tasksTab === "mine"
                  ? recentTasks.filter((t) => {
                      const task = t as Task & {
                        claimed_by?: string;
                        assignment_type?: string;
                      };
                      return (
                        task.assigned_to === user?.id ||
                        task.claimed_by === user?.id ||
                        task.assignment_type === "open_to_all"
                      );
                    })
                  : recentTasks;
              return displayedTasks.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Geen openstaande taken</p>
                <p className="text-sm text-gray-400">
                  {tasksTab === "mine"
                    ? "Er zijn geen taken aan jou toegewezen."
                    : "Goed gedaan! Alle taken zijn voltooid."}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {displayedTasks.map((task) => {
                  const TaskIcon = getTaskTypeIcon(task.type);
                  return (
                    <div
                      key={task.id}
                      className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex-shrink-0">
                        <TaskIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <div className="ml-3 flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {task.title}
                        </p>
                        <div className="flex items-center mt-1 space-x-2">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}
                          >
                            {task.priority === "urgent"
                              ? "urgent"
                              : task.priority === "high"
                                ? "Hoog"
                                : task.priority === "medium"
                                  ? "Gemiddeld"
                                  : "Laag"}
                          </span>
                          <span className="text-xs text-gray-500">
                            Vervalt {formatDueDate(task.due_date)}
                          </span>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}
                        >
                          {task.status === "pending"
                            ? "openstaand"
                            : task.status === "in_progress"
                              ? "bezig"
                              : task.status === "completed"
                                ? "voltooid"
                                : "geannuleerd"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
            })()}
          </div>
        </div>

        {/* Recent Communications */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Recente Communicatie
              </h2>
              <button
                onClick={() => onPageChange?.("communications")}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center"
              >
                Alles bekijken
                <ArrowRightIcon className="ml-1 h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="p-6">
            {recentCommunications.length === 0 ? (
              <div className="text-center py-8">
                <ChatBubbleLeftRightIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Geen recente communicatie</p>
                <button
                  onClick={() => onPageChange?.("communications")}
                  className="mt-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Nieuw aanmaken
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {recentCommunications.map((comm) => (
                  <div
                    key={comm.id}
                    className="flex items-start p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-shrink-0">
                      <div
                        className={`p-2 rounded-lg ${
                          comm.type === "urgent"
                            ? "bg-red-100 text-red-600"
                            : comm.type === "action_required"
                              ? "bg-orange-100 text-orange-600"
                              : "bg-primary-100 text-primary-600"
                        }`}
                      >
                        <ChatBubbleLeftRightIcon className="h-4 w-4" />
                      </div>
                    </div>
                    <div className="ml-3 flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {comm.title}
                      </p>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                        {comm.message}
                      </p>
                      <p className="text-xs text-gray-400 mt-2">
                        {format(new Date(comm.created_at), "MMM dd, HH:mm")}
                      </p>
                    </div>
                    {comm.is_pinned && (
                      <div className="flex-shrink-0">
                        <div className="h-2 w-2 bg-primary-600 rounded-full"></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Snelle Acties</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              {
                name: "Nieuwe Communicatie",
                icon: ChatBubbleLeftRightIcon,
                color: "bg-primary-100 text-primary-600",
                page: "communications",
              },
              {
                name: "Balans controle inplannen",
                icon: ScaleIcon,
                color: "bg-green-100 text-green-600",
                page: "calibrations",
              },
              {
                name: "Controle vervaldata",
                icon: CalendarDaysIcon,
                color: "bg-yellow-100 text-yellow-600",
                page: "expiry-checks",
              },
              {
                name: "Klachtenbeheer",
                icon: ExclamationTriangleIcon,
                color: "bg-red-100 text-red-600",
                page: "complaints",
              },
              {
                name: "Bestelling Beheren",
                icon: ClipboardDocumentListIcon,
                color: "bg-blue-100 text-blue-600",
                page: "tasks",
              },
            ].map((action, index) => {
              const Icon = action.icon;
              return (
                <button
                  key={index}
                  onClick={() => onPageChange?.(action.page)}
                  className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className={`p-3 rounded-lg ${action.color} mb-2`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <span className="text-sm font-medium text-gray-900 text-center">
                    {action.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
